import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './EmployeeMonitoring.css';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getDistrict, getStates } from '../../../services/monthlyAttendanceService';
import { capitalizeFirstLetter } from '../../../utils/validations';
import {
    getEmployeeMonitoring,
    getAllEmployeesTracking
} from '../../../services/employeePostingService';

const EmployeeMonitoring = () => {
    const currentYear = new Date().getFullYear();
    const [state, setState] = useState(3);
    const [district, setDistrict] = useState(null);
    const [date, setDate] = useState('');
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [districtId, setDistrictId] = useState("");

    const [attendanceData, setAttendanceData] = useState([]);
    const [employeeMonitoringData, setEmployeeMonitoringData] = useState([]);

    // ⬇️ NEW: view control for drill-down
    const [view, setView] = useState('summary'); // 'summary' | 'detail'
    const [selectedDistrict, setSelectedDistrict] = useState(null); // { districtId, districtName }
    const [loadingDetail, setLoadingDetail] = useState(false);

    const effectRan = useRef(false);
    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        date: false,
    });
    const [errors, setErrors] = useState({});
    const yearInputRef = useRef(null);

    const isFetchDataEnabled = state && date;

    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        };
    }, []);

    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    console.log("DISTRICT API RAW:", data);

                    // ⭐ handle multiple possible shapes
                    let districts = [];
                    if (Array.isArray(data)) districts = data;
                    else if (Array.isArray(data?.districts)) districts = data.districts;
                    else if (Array.isArray(data?.data)) districts = data.data;
                    else if (Array.isArray(data?.result)) districts = data.result;

                    const list = districts.map((d) => ({
                        value: d.districtId,
                        label: capitalizeFirstLetter(d.districtName),
                    }));

                    setDistrictOptions([
                        { value: "ALL", label: "All Districts" },
                        ...list,
                    ]);
                } catch (error) {
                    console.error("Error fetching districts:", error);
                    setDistrictOptions([
                        { value: "ALL", label: "All Districts" },
                    ]);
                    setErrors((prev) => ({
                        ...prev,
                        general: "Failed to load districts",
                    }));
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
            setDistrictId("");
        }
    }, [state]);
    // Clear data when filters change
    useEffect(() => {
        setAttendanceData([]);
        setEmployeeMonitoringData([]);
        setView('summary');
        setSelectedDistrict(null);
    }, [state, district, date]);

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                break;
            case 'district':
                if (value === "ALL") {
                    setDistrict("ALL");          // Reset district
                    setDistrictId("");
                } else {
                    setDistrict(value);
                    setDistrictId(value);
                }
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleYearChange = (date) => {
        setDate(date);
        setErrors(prev => ({ ...prev, date: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = 'State is required';
        if (!date) newErrors.date = 'Year is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const data = await getEmployeeMonitoring(state, district, date);
            setEmployeeMonitoringData(data || []);
            // reset drill-down whenever a new summary is generated
            setAttendanceData([]);
            setSelectedDistrict(null);
            setView('summary');
        } catch (error) {
            console.error('Error fetching report data:', error);
            setErrors(prev => ({
                ...prev,
                general: 'Failed to fetch report data',
            }));
            alert('Failed to fetch report data. Please try again.');
        }
    };

    // ⬇️ Called when a row in the SUMMARY table is clicked → drill-down
    const handleDrillDown = async (row) => {
        if (!validateForm()) return;

        setLoadingDetail(true);
        setSelectedDistrict({
            districtId: row.districtId,
            districtName: row.districtName,
        });

        try {
            const data = await getAllEmployeesTracking(state, row.districtId, date);
            setAttendanceData(data || []);
            setView('detail'); // 👈 switch to detail view
        } catch (error) {
            console.error('Error fetching drill-down data:', error);
            setErrors(prev => ({
                ...prev,
                general: 'Failed to fetch drill-down data',
            }));
            alert('Failed to fetch drill-down data. Please try again.');
        } finally {
            setLoadingDetail(false);
        }
    };

    // ⬇️ Back button handler
    const handleBack = () => {
        setView('summary');
        setAttendanceData([]);
        setSelectedDistrict(null);
    };

    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);

    return (
        <div className="monitoring-reportrarel-container">
            <div className="monitoring-reportrarel-box">
                <h2 className="monitoring-reportrarel-heading">Employee Monitoring</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="monitoring-reportrarel-maincontainer">
                        <div className="monitoring-reportrarel-container-part">
                            <div className={`monitoring-reportrarel-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="monitoring-reportrarel-container-part">
                            <div className={`monitoring-reportrarel-floating-label-container ${isFocused.district || district ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>District</label>
                            </div>
                        </div>
                    </div>

                    <div className="monitoring-reportrarel-maincontainer">
                        <div className="monitoring-reportrarel-container-part">
                            <div className={`monitoring-reportrarel-floating-label-container ${isFocused.date || date ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={date}
                                    onChange={(date) => handleYearChange(date)}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, date: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, date: false }))}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText=""
                                    className="monitoring-reportrarel-date-picker"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    ref={yearInputRef}
                                    title="Select a date"
                                />
                                <label><span className="required">*</span>Date</label>
                                {errors.date && <div className="error-message">{errors.date}</div>}
                            </div>
                        </div>
                        <div className="monitoring-reportrarel-container-part"></div>
                    </div>

                    <div className="monitoring-reportrarel-btn-div">
                        <button
                            type="submit"
                            className="monitoring-reportrarel-btn"
                            disabled={!isFetchDataEnabled}
                        >
                            Generate Report
                        </button>
                    </div>
                </form>
            </div>

            {/* ============ SUMMARY VIEW ============ */}
            {view === 'summary' && (
                <div className="reportrarel-table-monthlt-att-container">
                    <table className="reportrarel-table-monthlt-att">
                        <thead>
                            <tr>
                                <th className="sticky-col sno-col">S.No</th>
                                <th className="sticky-col name-col">District</th>
                                <th className="sticky-col name-col">Employee Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeMonitoringData.length > 0 ? (
                                employeeMonitoringData.map((ec, empIdx) => (
                                    <tr
                                        key={ec.districtId || empIdx}
                                        className="clickable-row"
                                        onClick={() => handleDrillDown(ec)}
                                    >
                                        <td className="sticky-col sno-col">{empIdx + 1}</td>
                                        <td className="sticky-col name-col">{ec.districtName}</td>
                                        <td className="sticky-col name-col">{ec.flaggedEmployeeCount}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="no-data-cell">
                                        No data available. Please generate a report.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ============ DETAIL VIEW ============ */}
            {view === 'detail' && (
                <>
                    {/* ⬅️ Back button + breadcrumb */}
                    <div className="drilldown-header">

                        {selectedDistrict && (
                            <span className="drilldown-title">
                                Employees in <strong>{selectedDistrict.districtName}</strong>
                            </span>
                        )}

                        <button
                            type="button"
                            className="back-btn"
                            onClick={handleBack}
                        >
                            ← Back
                        </button>
                    </div>

                    <div className="reportrarel-table-monthlt-att-container">
                        {loadingDetail ? (
                            <div className="loading-cell">Loading employees…</div>
                        ) : (
                            <table className="reportrarel-table-monthlt-att">
                                <thead>
                                    <tr>
                                        <th rowSpan="3" className="sticky-col sno-col">S.No</th>
                                        <th rowSpan="3" className="sticky-col name-col">Employee Name</th>
                                        <th rowSpan="3" className="sticky-col name-col">Designation</th>
                                        <th rowSpan="3" className="sticky-col loc-col">Location</th>
                                        <th rowSpan="3" className="sticky-col loc-col">Attendance Status</th>
                                        <th colSpan="48" className="deviation-header">
                                            Deviation From Job Location
                                        </th>
                                    </tr>
                                    <tr>
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <th key={i + 1} colSpan="2" className="hour-header">
                                                {String(i).padStart(2, '0')}:00 – {String(i + 1).padStart(2, '0')}:00 hr
                                            </th>
                                        ))}
                                    </tr>
                                    <tr>
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <React.Fragment key={`sub-${i}`}>
                                                <th className="sub-header">00 – 30 min</th>
                                                <th className="sub-header">30 – 60 min</th>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {attendanceData.length > 0 ? (
                                        attendanceData.map((employee, empIdx) => (
                                            <tr key={employee.employeeUniqueId || empIdx}>
                                                <td className="sticky-col sno-col">{empIdx + 1}</td>
                                                <td className="sticky-col name-col">{employee.employeeName}</td>
                                                <td className="sticky-col name-col">{employee.designation}</td>
                                                <td className="sticky-col loc-col">{employee.locationName}</td>
                                                <td className="sticky-col loc-col">{employee.attendanceStatus}</td>

                                                {employee.timeline?.map((hourBlock) =>
                                                    hourBlock.slots?.map((slot) => {
                                                        const firstRecord = slot.records?.[0];
                                                        const rawValue = firstRecord?.distanceFromPreviousMeters;

                                                        return (
                                                            <td
                                                                key={slot.timeWindow}
                                                                className={`distance-cell ${rawValue ? 'has-distance' : ''}`}
                                                                title={`${slot.timeWindow} | Records: ${slot.records?.length || 0} | Distance: ${rawValue ?? 0}`}
                                                            >
                                                                {rawValue !== undefined && rawValue !== null ? `${rawValue} km` : '-'}
                                                            </td>
                                                        );
                                                    })
                                                )}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="54" className="no-data-cell">
                                                No employee data available for this district.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default EmployeeMonitoring;